import { useMemo, useState, useRef, useLayoutEffect, useEffect } from 'react';
import { X } from 'lucide-react';
import type { CountryProfileIndicator } from '@upbeat/shared';
import { fmtValueParts } from '../lib/format';
import { useTheme } from '../components/theme-provider';
import { DEFAULT_COMPARE_COLORS, getCompareColor, getComponentColor } from './palette';
import { t } from '../i18n';

export interface RadarSeries {
  country: string;
  color: string;
  indicators: CountryProfileIndicator[];
  iso3c?: string | null;
}

export interface CanonicalIndicator {
  indicator: string;
  component: string;
  dimension: string;
  units?: string | null;
  inverted?: boolean;
  description?: string | null;
  formula?: string | null;
  unit?: string | null;
}

interface Props {
  series?: RadarSeries[];
  indicators?: CountryProfileIndicator[];
  countryName?: string;
  selectedIndicator?: string;
  size?: number;
  allIndicators?: CanonicalIndicator[];
}

/** Autonomous axis label formatter: caps length dynamically without hardcoded indicator mappings */
export function formatAxisLabel(name: string, maxLen = 22): string {
  if (!name) return '';
  const trimmed = name.trim();
  if (trimmed.length <= maxLen) return trimmed;
  return trimmed.slice(0, maxLen - 1).trimEnd() + '…';
}

/** Crisp pie-wedge ('quesito') icon mimicking the radar chart circular sector */
export function QuesitoIcon({
  color,
  size = 12,
  fillOpacity,
  strokeOpacity,
}: {
  color: string;
  size?: number;
  fillOpacity?: number;
  strokeOpacity?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 12"
      className="shrink-0 inline-block align-middle"
      aria-hidden="true"
    >
      <path
        d="M 6 11 L 1.2 3.5 A 8.9 8.9 0 0 1 10.8 3.5 Z"
        fill={color}
        fillOpacity={fillOpacity ?? 0.85}
        stroke={color}
        strokeWidth={0.75}
        strokeOpacity={strokeOpacity ?? 1}
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function RadarChart({
  series: rawSeries,
  indicators,
  countryName,
  selectedIndicator,
  size = 520,
  allIndicators,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const series: RadarSeries[] = useMemo(() => {
    if (rawSeries && rawSeries.length > 0) return rawSeries;
    if (indicators && countryName) {
      return [{ country: countryName, color: getCompareColor(0), indicators }];
    }
    return [];
  }, [rawSeries, indicators, countryName]);
  const [hoveredAxisIdx, setHoveredAxisIdx] = useState<number | null>(null);
  const [pinnedAxisIdx, setPinnedAxisIdx] = useState<number | null>(null);
  const activeAxisIdx = pinnedAxisIdx !== null ? pinnedAxisIdx : hoveredAxisIdx;
  const isPinned = pinnedAxisIdx !== null;
  const [pointerPos, setPointerPos] = useState<{ x: number; y: number } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverHeight, setPopoverHeight] = useState(260);
  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === 'light';

  useLayoutEffect(() => {
    if (popoverRef.current) {
      const h = popoverRef.current.offsetHeight;
      if (h && Math.abs(h - popoverHeight) > 8) {
        setPopoverHeight(h);
      }
    }
  }, [activeAxisIdx, popoverHeight]);

  // Close popover when clicking outside the radar container or pressing Escape
  useEffect(() => {
    if (activeAxisIdx === null) return;
    const onDocPointerDown = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setPinnedAxisIdx(null);
        setHoveredAxisIdx(null);
        setPointerPos(null);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPinnedAxisIdx(null);
        setHoveredAxisIdx(null);
        setPointerPos(null);
      }
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [activeAxisIdx]);

  // SVG dimensions
  const width = 640;
  const height = 500;
  const cx = width / 2;
  const cy = height / 2;
  const radius = 140;

  // Concentric levels (20%, 40%, 60%, 80%, 100% Excellence Frontier)
  const levels = [0.2, 0.4, 0.6, 0.8, 1.0];

  // Fixed canonical indicators list grouped by Dimension -> Component -> Indicator
  const indicatorsList = useMemo(() => {
    const baseMap = new Map<string, CountryProfileIndicator>();

    // 1. Seed from allIndicators (e.g. from meta) for guaranteed fixed canonical layout
    if (allIndicators && allIndicators.length > 0) {
      for (const item of allIndicators) {
        baseMap.set(item.indicator, {
          indicator: item.indicator,
          component: item.component,
          dimension: item.dimension,
          units: item.units ?? null,
          inverted: item.inverted ?? false,
          country_median: 0,
          africa_median: null,
          percentile_rank: 0,
          normalized_score: 50,
          n_utilities: 0,
          min_value: 0,
          max_value: 0,
          utilities: [],
          description: item.description ?? null,
          formula: item.formula ?? null,
          unit: item.unit ?? null,
        });
      }
    }

    // 2. Merge indicators from series to enrich metadata
    for (const s of series) {
      for (const ind of s.indicators) {
        if (!baseMap.has(ind.indicator)) {
          baseMap.set(ind.indicator, ind);
        } else {
          const existing = baseMap.get(ind.indicator)!;
          if (!existing.description && ind.description) existing.description = ind.description;
          if (!existing.formula && ind.formula) existing.formula = ind.formula;
          if (!existing.unit && ind.unit) existing.unit = ind.unit;
          if (existing.africa_median === null && ind.africa_median !== null) {
            existing.africa_median = ind.africa_median;
          }
        }
      }
    }

    const all = Array.from(baseMap.values());
    // Fixed canonical grouping: Dimension -> Component -> Indicator
    all.sort((a, b) => {
      if (a.dimension !== b.dimension) return a.dimension.localeCompare(b.dimension);
      if (a.component !== b.component) return a.component.localeCompare(b.component);
      return a.indicator.localeCompare(b.indicator);
    });

    return all;
  }, [allIndicators, series]);

  const n = indicatorsList.length;

  // Compute angles and axis points with non-colliding quadrant separation
  const axes = useMemo(() => {
    if (n < 3) return [];
    const angleStep = (Math.PI * 2) / n;
    return indicatorsList.map((ind, i) => {
      const angle = i * angleStep - Math.PI / 2;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);

      const ox = cx + radius * cos;
      const oy = cy + radius * sin;

      // Clean, non-colliding text-anchor alignment based strictly on x-direction:
      // Positive cos flows RIGHT ('start'), negative cos flows LEFT ('end')
      // Only the exact vertical meridian axis (|cos| <= 0.05) centers ('middle')
      let textAnchor: 'start' | 'middle' | 'end' = 'middle';
      if (cos > 0.05) {
        textAnchor = 'start';
      } else if (cos < -0.05) {
        textAnchor = 'end';
      }

      let lx = cx + (radius + 20) * cos;
      let ly = cy + (radius + 18) * sin;

      // Relative offset from Top Center (12 o'clock, i=0)
      const kTop = i <= n / 2 ? i : i - n;

      // Relative offset from Bottom Center (6 o'clock, i=mid)
      const mid = Math.round(n / 2);
      let kBottom = i - mid;
      if (kBottom > n / 2) kBottom -= n;
      if (kBottom < -n / 2) kBottom += n;

      // Vertical & horizontal stagger for the top & bottom poles (0, ±1, ±2)
      // to guarantee at least 20px vertical clearance between adjacent labels:
      const dominantBaseline: 'central' | 'auto' | 'hanging' = 'central';
      if (kTop === 0) {
        // Top Center (0): Age of payables
        ly = cy - (radius + 46);
        textAnchor = 'middle';
      } else if (Math.abs(kTop) === 1) {
        // Top ±1: Age of receivables (+1) and Transmission Losses (-1)
        ly = cy - (radius + 26);
        if (cos > 0) lx = Math.max(cx + 42, lx);
        else lx = Math.min(cx - 42, lx);
      } else if (Math.abs(kTop) === 2) {
        // Top ±2: Capital expenditure (+2) and System Losses (-2)
        // Set ly so delta-y to ±1 is 20px and delta-y to ±3 is 18px
        ly = cy - (radius + 6);
        if (cos > 0) lx = Math.max(cx + 64, lx);
        else lx = Math.min(cx - 64, lx);
      } else if (kBottom === 0) {
        // Bottom Center (0): Net Debt to EBITDA
        ly = cy + (radius + 46);
        textAnchor = 'middle';
      } else if (Math.abs(kBottom) === 1) {
        // Bottom ±1: Maturity matching (+1) and Net debt to sales ratio (-1)
        ly = cy + (radius + 26);
        if (cos > 0) lx = Math.max(cx + 42, lx);
        else lx = Math.min(cx - 42, lx);
      } else if (Math.abs(kBottom) === 2) {
        // Bottom ±2: Interest Coverage Ratio (+2) and Net Profit Margin (-2)
        // Set ly so delta-y to ±1 is 20px and delta-y to ±3 is 18px
        ly = cy + (radius + 6);
        if (cos > 0) lx = Math.max(cx + 64, lx);
        else lx = Math.min(cx - 64, lx);
      }

      return {
        index: i,
        indicator: ind,
        angle,
        cos,
        sin,
        ox,
        oy,
        lx,
        ly,
        textAnchor,
        dominantBaseline,
      };
    });
  }, [indicatorsList, n, cx, cy, radius]);

  // Background sectors for each component category
  const componentSectors = useMemo(() => {
    if (axes.length === 0) return [];

    const sectors: Array<{
      component: string;
      dimension: string;
      color: string;
      startIndex: number;
      endIndex: number;
      path: string;
    }> = [];

    const angleStep = (Math.PI * 2) / axes.length;
    let currentComp = axes[0]!.indicator.component;
    let currentDim = axes[0]!.indicator.dimension;
    let startIdx = 0;

    for (let i = 1; i <= axes.length; i++) {
      const isEnd = i === axes.length || axes[i]!.indicator.component !== currentComp;
      if (isEnd) {
        const endIdx = i - 1;
        const startAngle = (startIdx - 0.5) * angleStep - Math.PI / 2;
        const endAngle = (endIdx + 0.5) * angleStep - Math.PI / 2;
        const arcRadius = radius;

        const x1 = cx + arcRadius * Math.cos(startAngle);
        const y1 = cy + arcRadius * Math.sin(startAngle);
        const x2 = cx + arcRadius * Math.cos(endAngle);
        const y2 = cy + arcRadius * Math.sin(endAngle);
        const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;

        const path = `M ${cx} ${cy} L ${x1} ${y1} A ${arcRadius} ${arcRadius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

        sectors.push({
          component: currentComp,
          dimension: currentDim,
          color: getComponentColor(currentComp),
          startIndex: startIdx,
          endIndex: endIdx,
          path,
        });

        if (i < axes.length) {
          currentComp = axes[i]!.indicator.component;
          currentDim = axes[i]!.indicator.dimension;
          startIdx = i;
        }
      }
    }

    return sectors;
  }, [axes, cx, cy, radius]);

  // Compute polygons for each country series
  const countryPolygons = useMemo(() => {
    if (axes.length < 3) return [];

    return series.map((s) => {
      const indMap = new Map(s.indicators.map((i) => [i.indicator, i]));

      const points = axes.map((axis) => {
        const found = indMap.get(axis.indicator.indicator);
        const score = found ? Math.max(5, Math.min(100, found.normalized_score)) : 50;
        const r = (radius * score) / 100;
        const x = cx + r * axis.cos;
        const y = cy + r * axis.sin;
        return {
          x,
          y,
          score,
          indicator: found ?? null,
          hasData: Boolean(found),
        };
      });

      // Connect only valid data points directly without dipping into missing indicators
      const validPoints = points.filter((p) => p.hasData);
      let path = '';
      if (validPoints.length >= 3) {
        path = validPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
      } else if (validPoints.length === 2 && validPoints[0] && validPoints[1]) {
        path = `M ${validPoints[0].x} ${validPoints[0].y} L ${validPoints[1].x} ${validPoints[1].y}`;
      }

      return {
        country: s.country,
        color: s.color,
        path,
        points,
        validCount: validPoints.length,
      };
    });
  }, [series, axes, cx, cy, radius]);

  // Dynamic floating popover coordinates clamped to viewport and container
  const popoverStyle = useMemo(() => {
    if (!pointerPos || !containerRef.current) return null;

    const containerRect = containerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Uniform width clamped to viewport with 12px margin on mobile
    const popoverWidth = Math.min(340, viewportWidth - 24);
    const padding = 16;

    // Pointer position in viewport coordinates
    const clientX = containerRect.left + pointerPos.x;
    const clientY = containerRect.top + pointerPos.y;

    // 1. Horizontal positioning:
    // If pointer is in the right half of the container, open towards the left; otherwise towards the right.
    let targetClientX: number;
    if (pointerPos.x > containerRect.width / 2) {
      targetClientX = clientX - popoverWidth - padding;
    } else {
      targetClientX = clientX + padding;
    }

    // Clamp horizontally to stay inside viewport (with 12px margin)
    const minClientX = 12;
    const maxClientX = Math.max(minClientX, viewportWidth - popoverWidth - 12);
    const clampedClientX = Math.max(minClientX, Math.min(maxClientX, targetClientX));

    // 2. Vertical positioning:
    // When hovering bottom indicators or when space below in viewport is tight, open UPWARDS into chart
    const spaceBelow = viewportHeight - clientY;
    let targetClientY: number;

    if (pointerPos.y > containerRect.height * 0.48 || spaceBelow < popoverHeight + 24) {
      // Open above the cursor / upward into the chart space
      targetClientY = clientY - popoverHeight - 10;
    } else {
      // Open below the cursor
      targetClientY = clientY + 10;
    }

    // Clamp vertically so the popover is NEVER cut off off-screen (never below viewport, never above viewport)
    const minClientY = 12;
    const maxClientY = Math.max(minClientY, viewportHeight - popoverHeight - 12);
    const clampedClientY = Math.max(minClientY, Math.min(maxClientY, targetClientY));

    // Convert clamped viewport coordinates back to container relative coordinates
    return {
      left: `${clampedClientX - containerRect.left}px`,
      top: `${clampedClientY - containerRect.top}px`,
      width: `${popoverWidth}px`,
    };
  }, [pointerPos, popoverHeight]);

  if (n < 3 || series.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-xs text-dim">
        Select countries to display the multidimensional radar comparison
      </div>
    );
  }

  const activeAxis = activeAxisIdx !== null ? axes[activeAxisIdx] : null;

  return (
    <div ref={containerRef} className="relative flex flex-col items-center select-none w-full max-w-[680px] mx-auto">
      <svg
        viewBox="0 50 640 400"
        className="w-full h-auto overflow-visible cursor-crosshair"
        role="img"
        aria-label={t.radarAria}
        onClick={() => {
          setPinnedAxisIdx(null);
          setHoveredAxisIdx(null);
          setPointerPos(null);
        }}
        onPointerMove={(e) => {
          if (pinnedAxisIdx !== null) return;
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            setPointerPos({
              x: e.clientX - rect.left,
              y: e.clientY - rect.top,
            });
          }
        }}
        onPointerLeave={() => {
          if (pinnedAxisIdx === null) {
            setHoveredAxisIdx(null);
            setPointerPos(null);
          }
        }}
      >
        {/* Category / Component Sectors: soft borderless background shading matching country opacity */}
        <g className="category-sectors">
          {componentSectors.map((sec) => (
            <path
              key={sec.component}
              d={sec.path}
              fill={sec.color}
              fillOpacity={isLight ? 0.125 : 0.175}
              stroke="none"
              className="transition-opacity duration-200"
            />
          ))}
        </g>

        {/* True Circular Concentric Grid Levels (20%, 40%, 60%, 80%, 100%) */}
        <g className="grid-circles">
          {levels.map((lvl) => {
            const r = radius * lvl;
            const isExcellence = lvl === 1.0;
            return (
              <circle
                key={lvl}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={
                  isExcellence
                    ? isLight
                      ? 'rgba(0,0,0,0.22)'
                      : 'rgba(255,255,255,0.24)'
                    : isLight
                    ? 'rgba(0,0,0,0.08)'
                    : 'rgba(255,255,255,0.09)'
                }
                strokeWidth={isExcellence ? 1.2 : 0.8}
                strokeDasharray={isExcellence ? undefined : '3 3'}
              />
            );
          })}
        </g>

        {/* Radial Axes */}
        <g className="axes">
          {axes.map((a, i) => {
            const isHovered = activeAxisIdx === i;
            const isSelected = selectedIndicator === a.indicator.indicator;
            return (
              <g key={i}>
                <line
                  x1={cx}
                  y1={cy}
                  x2={a.ox}
                  y2={a.oy}
                  stroke={
                    isSelected
                      ? isLight
                        ? '#64748B'
                        : '#94A3B8'
                      : isHovered
                      ? isLight
                        ? '#475569'
                        : '#94A3B8'
                      : isLight
                      ? 'rgba(0,0,0,0.08)'
                      : 'rgba(255,255,255,0.08)'
                  }
                  strokeWidth={isSelected ? 1.3 : isHovered ? 1.1 : 0.6}
                  strokeDasharray={isSelected ? '4 3' : undefined}
                  className="transition-colors duration-150"
                />
                {isSelected && (
                  <circle
                    cx={a.ox}
                    cy={a.oy}
                    r={2.5}
                    fill="var(--chart-axis)"
                    stroke="var(--space)"
                    strokeWidth={0.8}
                  />
                )}
              </g>
            );
          })}
        </g>

        {/* Country Polygons (layered with subtle, light opacity) */}
        {countryPolygons.map((cp) => (
          <g key={cp.country}>
            {/* Filled Polygon */}
            {cp.path && (
              <path
                d={cp.path}
                fill={cp.validCount >= 3 ? cp.color : 'none'}
                fillOpacity={cp.validCount >= 3 ? 0.05 : 0}
                stroke={cp.color}
                strokeWidth={1.3}
                strokeOpacity={0.75}
                strokeLinejoin="round"
                className="transition-all duration-300"
              />
            )}

            {/* Vertices */}
            {cp.points.map((p, i) => {
              if (!p.hasData) return null;
              const isHovered = activeAxisIdx === i;
              const isSelected = selectedIndicator === axes[i]?.indicator.indicator;
              return (
                <g
                  key={i}
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = containerRef.current?.getBoundingClientRect();
                    if (rect) {
                      setPointerPos({
                        x: e.clientX - rect.left,
                        y: e.clientY - rect.top,
                      });
                    }
                    if (pinnedAxisIdx === i) {
                      setPinnedAxisIdx(null);
                      setHoveredAxisIdx(null);
                    } else {
                      setPinnedAxisIdx(i);
                      setHoveredAxisIdx(i);
                    }
                  }}
                >
                  {isSelected && (
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={5.5}
                      fill="none"
                      stroke={cp.color}
                      strokeWidth={1.2}
                      strokeDasharray="2 2"
                      strokeOpacity={0.65}
                      className="animate-pulse"
                    />
                  )}
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={isHovered ? 4.5 : isSelected ? 3.5 : 2.4}
                    fill={cp.color}
                    fillOpacity={0.85}
                    stroke="var(--space)"
                    strokeWidth={1}
                    className="transition-all duration-150"
                  />
                </g>
              );
            })}
          </g>
        ))}

        {/* Axis Labels (Perimeter) & Interactive Transparent Hover Wedges */}
        <g className="labels">
          {axes.map((a, i) => {
            const isHovered = activeAxisIdx === i;
            const isSelected = selectedIndicator === a.indicator.indicator;
            const shortName = formatAxisLabel(a.indicator.indicator);

            return (
              <g
                key={i}
                className="cursor-pointer"
                onMouseEnter={() => {
                  if (pinnedAxisIdx === null) setHoveredAxisIdx(i);
                }}
                onMouseLeave={() => {
                  if (pinnedAxisIdx === null) setHoveredAxisIdx(null);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = containerRef.current?.getBoundingClientRect();
                  if (rect) {
                    setPointerPos({
                      x: e.clientX - rect.left,
                      y: e.clientY - rect.top,
                    });
                  }
                  if (pinnedAxisIdx === i) {
                    setPinnedAxisIdx(null);
                    setHoveredAxisIdx(null);
                  } else {
                    setPinnedAxisIdx(i);
                    setHoveredAxisIdx(i);
                  }
                }}
              >
                {/* Invisible wide line for easy hover target */}
                <line
                  x1={cx}
                  y1={cy}
                  x2={a.ox}
                  y2={a.oy}
                  stroke="transparent"
                  strokeWidth={24}
                />

                <text
                  x={a.lx}
                  y={a.ly}
                  textAnchor={a.textAnchor}
                  dominantBaseline={a.dominantBaseline}
                  className={`text-[10px] tracking-tight transition-colors duration-150 ${
                    isSelected
                      ? isLight
                        ? 'fill-slate-700 font-bold text-[10.5px]'
                        : 'fill-slate-200 font-bold text-[10.5px]'
                      : isHovered
                      ? isLight
                        ? 'fill-slate-800 font-bold text-[10.5px]'
                        : 'fill-slate-200 font-bold text-[10.5px]'
                      : isLight
                      ? 'fill-slate-600 font-medium'
                      : 'fill-slate-400 font-medium'
                  }`}
                >
                  {isSelected ? `★ ${shortName}` : shortName}
                </text>
              </g>
            );
          })}
        </g>
      </svg>

      {/* Floating popover tooltip on hover or tap - ZERO layout displacement, viewport-clamped */}
      {activeAxis && pointerPos && popoverStyle && (
        <div
          ref={popoverRef}
          className="pointer-events-auto absolute z-50 p-3 bg-popover/95 border border-hair rounded-lg text-xs shadow-2xl backdrop-blur-xl animate-in fade-in-0 zoom-in-95 duration-75 space-y-2 select-none max-h-[85vh] overflow-y-auto"
          style={popoverStyle}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="space-y-1.5 pb-2 border-b border-hair/60">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-bold text-txt break-words whitespace-normal text-xs leading-snug">
                  {activeAxis.indicator.indicator}
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-dim mt-0.5 break-words whitespace-normal">
                  <QuesitoIcon
                    color={getComponentColor(activeAxis.indicator.component)}
                    size={11}
                    fillOpacity={isLight ? 0.055 : 0.075}
                    strokeOpacity={isLight ? 0.7 : 0.85}
                  />
                  <span>
                    {activeAxis.indicator.component} · {activeAxis.indicator.dimension}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-1.5 shrink-0">
                {activeAxis.indicator.inverted ? (
                  <span className="text-[9.5px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30 font-medium">
                    ↓ {t.mapBetter(true)}
                  </span>
                ) : (
                  <span className="text-[9.5px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 font-medium">
                    ↑ {t.mapBetter(false)}
                  </span>
                )}
                {isPinned && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPinnedAxisIdx(null);
                      setHoveredAxisIdx(null);
                      setPointerPos(null);
                    }}
                    className="text-dim hover:text-txt p-1 rounded -mr-1"
                    aria-label="Close"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Indicator Description from UPBEAT */}
            {activeAxis.indicator.description && (
              <p className="text-[10.5px] text-muted-foreground leading-snug">
                {activeAxis.indicator.description}
              </p>
            )}

            {/* Calculation Formula from UPBEAT */}
            {activeAxis.indicator.formula && (
              <div className="text-[9.5px] font-mono text-dim bg-muted/40 p-1.5 rounded border border-hair/60 break-words leading-tight">
                <span className="font-semibold text-txt">{t.formula}:</span> {activeAxis.indicator.formula}
              </div>
            )}
          </div>

          {/* Africa Median row if available */}
          {activeAxis.indicator.africa_median !== null && (
            <div className="flex items-center justify-between text-[11px] px-1 py-1 border-b border-hair/40 font-mono">
              <span className="text-dim">{t.africaBenchmark}:</span>
              <span className="font-semibold text-txt">
                {fmtValueParts(activeAxis.indicator.units, activeAxis.indicator.africa_median, activeAxis.indicator.unit).text}{' '}
                <span className="text-[9px] text-mut">{fmtValueParts(activeAxis.indicator.units, activeAxis.indicator.africa_median, activeAxis.indicator.unit).unit}</span>
              </span>
            </div>
          )}

          {/* Country breakdown rows */}
          <div className="space-y-1.5 pt-0.5">
            {series.map((s, idx) => {
              const poly = countryPolygons[idx];
              const pt = poly?.points[activeAxis.index];
              const ind = pt?.indicator;

              if (!ind) {
                return (
                  <div key={s.country} className="flex items-center justify-between text-[11px]">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="font-medium text-txt">{s.country}</span>
                      {s.iso3c && (
                        <span className="font-mono text-[9.5px] text-dim uppercase">({s.iso3c})</span>
                      )}
                    </div>
                    <span className="text-dim text-[10px]">{t.noDataCard}</span>
                  </div>
                );
              }

              const raw = fmtValueParts(ind.units, ind.country_median, ind.unit);
              const score = Math.round(ind.normalized_score);

              return (
                <div key={s.country} className="flex items-center justify-between text-[11px]">
                  <div className="flex items-center gap-1.5 min-w-0 pr-1">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                    <span className="font-medium text-txt truncate">{s.country}</span>
                    {s.iso3c && (
                      <span className="font-mono text-[9.5px] text-dim uppercase shrink-0">({s.iso3c})</span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-mono font-bold text-txt">
                      {raw.text} <span className="text-[9px] text-mut">{raw.unit}</span>
                    </span>
                    <span
                      className="font-mono text-[10px] font-semibold px-1.5 py-0.5 rounded bg-muted/30 border border-hair/40"
                      style={{ color: s.color }}
                    >
                      {t.score}: {score}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Unified Legend & Notes (Below Radar) */}
      <div className="w-full flex flex-col items-center gap-1.5 mt-0.5 pt-2 border-t border-hair/50 select-none text-center">
        {/* 1. Countries Series */}
        <div className="flex flex-wrap items-center justify-center gap-3.5 text-xs font-medium">
          {series.map((s) => (
            <div key={s.country} className="flex items-center gap-1.5">
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0 shadow-sm"
                style={{ backgroundColor: s.color }}
              />
              <span className="text-txt">{s.country}</span>
              {s.iso3c && (
                <span className="font-mono text-[10px] text-dim uppercase">
                  ({s.iso3c})
                </span>
              )}
            </div>
          ))}
        </div>

        {/* 2. Components / Dimensions */}
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[10.5px] text-dim">
          <span className="font-semibold text-txt uppercase text-[9.5px] tracking-wider">
            {t.components}:
          </span>
          {componentSectors.map((sec) => (
            <div key={sec.component} className="flex items-center gap-1.5">
              <QuesitoIcon
                color={sec.color}
                size={11}
                fillOpacity={isLight ? 0.055 : 0.075}
                strokeOpacity={isLight ? 0.7 : 0.85}
              />
              <span className="text-txt">{sec.component}</span>
            </div>
          ))}
        </div>

        {/* 3. Explanatory Note */}
        <div className="text-[10px] text-dim leading-normal">
          {t.radarFooterNote}
        </div>
      </div>
    </div>
  );
}
